/**
 * Two-factor authentication (TOTP) — wire contract.
 *
 * Every error is `{detail, code}`; the UI branches on `code` (and on the status
 * for the 429 rate limit), never on `detail`.
 */
export const TWO_FACTOR_ERROR_CODES = [
  'invalid_code',
  'challenge_invalid',
  'two_factor_already_enrolled',
  'two_factor_no_pending_enrollment',
  'two_factor_not_enrolled',
  'two_factor_disabled',
  'two_factor_required_by_policy',
  'privileged_user_cannot_be_exempt',
] as const

export type TwoFactorErrorCode = (typeof TWO_FACTOR_ERROR_CODES)[number]

/**
 * What a first-factor response (`jwt/create`, social login, activation) means.
 * A `200` no longer means "signed in": both challenge shapes carry NO cookies.
 */
export type FirstFactorOutcome =
  | { kind: 'session' }
  | { kind: 'verify'; challengeToken: string; expiresIn: number }
  | { kind: 'setup'; challengeToken: string; expiresIn: number }

export type TwoFactorRequirement = 'disabled' | 'optional' | 'required'

/** `GET /auth/2fa/` */
export interface TwoFactorStatus {
  enrolled: boolean
  confirmed_at: string | null
  recovery_codes_remaining: number
  /** The user's EFFECTIVE requirement, after the global mode and any override. */
  requirement: TwoFactorRequirement
}

/** Secret material: rendered once, never logged, never stored. */
export interface TwoFactorEnrollment {
  secret: string
  otpauth_uri: string
  /** A self-contained inline SVG. */
  qr_svg: string
}

export interface RecoveryCodesResponse {
  recovery_codes: string[]
}

export interface ChallengeCodePayload {
  challenge_token: string
  code: string
}

/** Enable (`{password}`) or rotate (`{password, code}` — a code from the CURRENT app). */
export interface EnrollPayload {
  password: string
  code?: string
}

export interface DisablePayload {
  password: string
  code: string
}
